import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Banknote, ShieldCheck, Globe, Info } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6KwTsMRtQr4BqY6ndtmpVt/Screenshot_2026-06-05_at_15.33.56.png';
const PATTERN_URL = 'https://cdn.magicpatterns.com/uploads/9uRyrqe84cmEa3qe5abkYU/Screenshot_2026-06-05_at_15.34.02.png';
type TabType = 'Investing' | 'Spread Betting' | 'CFD';
type SpreadCategory = 'Forex' | 'Indices' | 'Commodities' | 'Shares';
export function CostsAndCharges() {
  const [activeTab, setActiveTab] = useState<TabType>('Investing');
  const [activeSpreadCategory, setActiveSpreadCategory] = useState<SpreadCategory>('Forex');
  useEffect(() => {
    document.title = 'Equitrade Capital Investing, Spread Betting, CFD';
  }, []);
  const tabs: TabType[] = ['Investing', 'Spread Betting', 'CFD'];
  const spreadData: Record<SpreadCategory, {
    name: string;
    val: string;
  }[]> = {
    Forex: [{
      name: 'EUR/USD',
      val: '0.6'
    }, {
      name: 'EUR/GBP',
      val: '0.9'
    }, {
      name: 'GBP/USD',
      val: '0.9'
    }, {
      name: 'USD/JPY',
      val: '0.7'
    }],
    Indices: [{
      name: 'FTSE 100',
      val: '1'
    }, {
      name: 'S&P 500',
      val: '0.4'
    }, {
      name: 'US Tech 100',
      val: '1'
    }, {
      name: 'Germany 40',
      val: '1.4'
    }],
    Commodities: [{
      name: 'Spot Gold',
      val: '0.3'
    }, {
      name: 'Spot Silver (5000oz)',
      val: activeTab === 'CFD' ? '2' : '3'
    }, {
      name: 'Oil - Brent Crude',
      val: '2.8'
    }, {
      name: 'Natural Gas',
      val: '3'
    }],
    Shares: [{
      name: 'US (Major)',
      val: '0.10%'
    }, {
      name: 'UK (FTSE 100)',
      val: '0.10%'
    }, {
      name: 'Europe (major)',
      val: '0.10%'
    }, {
      name: 'Australia (major)',
      val: '0.10%'
    }]
  };
  const depositItems = [{
    label: 'Bank transfers:',
    value: 'Free'
  }, {
    label: 'Debit card deposits/withdrawals:',
    value: 'Free',
    sub: 'Limit of £99,999 deposit per transaction. Larger deposits will need to be broken down into separate transactions.'
  }, {
    label: 'Credit card deposits/withdrawals:',
    value: 'Free',
    sub: 'Limit of £30,000 deposit per transaction. Larger deposits will need to be broken down into separate transactions.'
  }];
  const spreadCategories: SpreadCategory[] = activeTab === 'CFD' ? ['Forex', 'Indices', 'Commodities'] : ['Forex', 'Indices', 'Commodities', 'Shares'];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className="min-h-screen bg-white font-sans text-slate-900" data-id="element-3">
      {/* Top Banner */}
      <div className="bg-[#1a1a1a] text-white py-3 px-4 text-[10px] sm:text-xs text-center leading-relaxed" data-id="element-4">
        <div className="max-w-7xl mx-auto" data-id="element-5">
          <p data-id="element-6">
            Spread bets and CFDs are complex instruments and come with a high
            risk of losing money rapidly due to leverage.{' '}
            <strong data-id="element-7">
              76% of retail investor accounts lose money when trading spread
              bets and CFDs with this provider.
            </strong>{' '}
            You should consider whether you understand how spread bets and CFDs
            work, and whether you can afford to take the high risk of losing
            your money.We use cookies to help us improve, promote, and protect
            our services.
          </p>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-slate-100 sticky top-0 bg-white z-50 shadow-sm" data-id="element-8">
        <div className="max-w-7xl mx-auto px-4 h-20 sm:h-32 flex items-center justify-center sm:justify-end" data-id="element-9">
          <img src={LOGO_URL} alt="Equitrade Capital" className="h-12 sm:h-24 object-contain" data-id="element-10" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 overflow-hidden bg-slate-50" data-id="element-11">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: `url(${PATTERN_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-12" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-13">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.6
        }} className="text-3xl sm:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-6 tracking-tight" data-id="element-14">
            Costs and charges
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto" data-id="element-15">
            Whether it's investing or trading, your focus should be on your next
            move, not decoding fees. That's why ours are simple, transparent,
            and upfront.
          </motion.p>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-20 sm:top-32 bg-white border-b border-slate-100 z-40" data-id="element-16">
        <div className="max-w-4xl mx-auto px-4" data-id="element-17">
          <div className="flex justify-start sm:justify-center space-x-2 py-4 sm:py-6 overflow-x-auto no-scrollbar" data-id="element-18">
            {tabs.map(tab => <button key={tab} onClick={() => {
            setActiveTab(tab);
            if (tab === 'CFD' && activeSpreadCategory === 'Shares') {
              setActiveSpreadCategory('Forex');
            }
          }} className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all ${activeTab === tab ? 'bg-sky-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-19">
                {tab}
              </button>)}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12 space-y-16 sm:space-y-24" data-id="element-20">
        <AnimatePresence mode="wait" data-id="element-21">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-22">
            {activeTab === 'Investing' ? <div className="space-y-16 sm:space-y-24" data-id="element-23">
                <section data-id="element-24">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-25">
                    Trading and account fees
                  </h2>
                  <div className="space-y-3 sm:space-y-4" data-id="element-26">
                    {[{
                  label: 'Commission:',
                  value: 'Free'
                }, {
                  label: 'Platform fee:',
                  value: 'Free'
                }, {
                  label: 'FX fee:',
                  value: '0.70%',
                  sub: "If you place a trade involving currency conversion, we'll convert your funds at the current exchange rate plus a 0.7% fee."
                }, {
                  label: 'Custody/inactivity fee:',
                  value: 'Free'
                }].map((item, i) => <div key={i} className="bg-slate-50 p-4 sm:p-6 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2" data-id="element-27">
                        <div className="max-w-xl" data-id="element-28">
                          <h4 className="font-bold text-sm" data-id="element-29">{item.label}</h4>
                          {item.sub && <p className="text-xs text-slate-500 mt-1" data-id="element-30">
                              {item.sub}
                            </p>}
                        </div>
                        <span className={`${item.value === 'Free' ? 'text-green-600' : 'text-sky-600'} font-bold text-sm whitespace-nowrap`} data-id="element-31">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </section>

                <section data-id="element-32">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-33">
                    Interest you can earn
                  </h2>
                  <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border-l-4 border-sky-600 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-34">
                    <div data-id="element-35">
                      <h4 className="font-bold text-sm mb-1" data-id="element-36">Interest rate:</h4>
                      <p className="text-xs text-slate-500" data-id="element-37">
                        Paid out monthly on uninvested cash up to £100,000.
                      </p>
                    </div>
                    <span className="text-green-600 font-bold text-lg whitespace-nowrap" data-id="element-38">
                      3.75% AER variable
                    </span>
                  </div>
                </section>

                <section data-id="element-39">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-40">
                    Deposits and withdrawals
                  </h2>
                  <div className="space-y-3 sm:space-y-4" data-id="element-41">
                    {depositItems.map((item, i) => <div key={i} className="bg-slate-50 p-4 sm:p-6 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2" data-id="element-42">
                        <div className="max-w-xl" data-id="element-43">
                          <h4 className="font-bold text-sm" data-id="element-44">{item.label}</h4>
                          {item.sub && <p className="text-xs text-slate-500 mt-1" data-id="element-45">
                              {item.sub}
                            </p>}
                        </div>
                        <span className="text-green-600 font-bold text-sm whitespace-nowrap" data-id="element-46">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </section>

                <section data-id="element-47">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-48">
                    Portfolio transfers
                  </h2>
                  <div className="bg-slate-50 p-4 sm:p-6 rounded-xl flex justify-between items-center" data-id="element-49">
                    <h4 className="font-bold text-sm" data-id="element-50">
                      Portfolio transfers in/out:
                    </h4>
                    <span className="text-green-600 font-bold text-sm" data-id="element-51">
                      Free
                    </span>
                  </div>
                </section>
              </div> : <div className="space-y-16 sm:space-y-24" data-id="element-52">
                <section data-id="element-53">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-54">
                    Trading fees
                  </h2>
                  <div className="bg-slate-50 rounded-2xl p-4 sm:p-8 space-y-8 sm:space-y-12" data-id="element-55">
                    <div className="space-y-6" data-id="element-56">
                      <div data-id="element-57">
                        <h3 className="text-lg font-bold mb-2" data-id="element-58">Spread:</h3>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-59">
                          Spreads are variable, depend on underlying market
                          conditions, and are built into the price shown when
                          submitting a trade.
                        </p>
                      </div>

                      <div className="flex space-x-4 sm:space-x-6 text-xs font-bold text-slate-400 border-b border-slate-200 pb-2 overflow-x-auto no-scrollbar" data-id="element-60">
                        {spreadCategories.map(cat => <button key={cat} onClick={() => setActiveSpreadCategory(cat)} className={`transition-all pb-2 whitespace-nowrap ${activeSpreadCategory === cat ? 'text-sky-600 border-b-2 border-sky-600' : 'hover:text-slate-600'}`} data-id="element-61">
                            {cat}
                          </button>)}
                      </div>

                      <div className="bg-slate-900 rounded-xl overflow-hidden text-white overflow-x-auto" data-id="element-62">
                        <table className="w-full text-sm min-w-[300px]" data-id="element-63">
                          <thead className="bg-slate-800 text-slate-300" data-id="element-64">
                            <tr data-id="element-65">
                              <th className="text-left py-4 px-4 sm:px-6 font-medium" data-id="element-66">
                                Instrument
                              </th>
                              <th className="text-right py-4 px-4 sm:px-6 font-medium" data-id="element-67">
                                {activeSpreadCategory === 'Shares' ? 'Minimum spread' : 'Minimum spread (bps)'}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/10" data-id="element-68">
                            {spreadData[activeSpreadCategory].map((row, i) => <tr key={i} data-id="element-69">
                                <td className="py-4 px-4 sm:px-6" data-id="element-70">
                                  {row.name}
                                </td>
                                <td className="py-4 px-4 sm:px-6 text-right" data-id="element-71">
                                  {row.val}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {activeTab === 'CFD' && <div className="space-y-6" data-id="element-72">
                        <h3 className="text-lg font-bold mb-2" data-id="element-73">Commission:</h3>
                        <p className="text-slate-600 text-sm leading-relaxed" data-id="element-74">
                          For Share trading, we charge a commission fee
                          proportional to transaction value.
                        </p>
                        <div className="bg-slate-900 rounded-xl overflow-hidden text-white overflow-x-auto" data-id="element-75">
                          <table className="w-full text-sm min-w-[300px]" data-id="element-76">
                            <thead className="bg-slate-800 text-slate-300" data-id="element-77">
                              <tr data-id="element-78">
                                <th className="text-left py-4 px-4 sm:px-6 font-medium" data-id="element-79">
                                  Instrument
                                </th>
                                <th className="text-right py-4 px-4 sm:px-6 font-medium" data-id="element-80">
                                  Minimum spread
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10" data-id="element-81">
                              <tr data-id="element-82">
                                <td className="py-4 px-4 sm:px-6" data-id="element-83">UK</td>
                                <td className="py-4 px-4 sm:px-6 text-right" data-id="element-84">
                                  0.10%
                                </td>
                              </tr>
                              <tr data-id="element-85">
                                <td className="py-4 px-4 sm:px-6" data-id="element-86">Euro</td>
                                <td className="py-4 px-4 sm:px-6 text-right" data-id="element-87">
                                  0.10%
                                </td>
                              </tr>
                              <tr data-id="element-88">
                                <td className="py-4 px-4 sm:px-6" data-id="element-89">US</td>
                                <td className="py-4 px-4 sm:px-6 text-right" data-id="element-90">
                                  2 cents / share
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>}

                    <div className="space-y-6" data-id="element-91">
                      <h3 className="text-lg font-bold mb-2" data-id="element-92">
                        Overnight funding:
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-4" data-id="element-93">
                        <div className="bg-white p-6 rounded-xl border border-slate-200 flex flex-col items-center text-center" data-id="element-94">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-sky-50 rounded-full flex items-center justify-center mb-4" data-id="element-95">
                            <Banknote className="text-sky-600 w-6 h-6 sm:w-8 sm:h-8" data-id="element-96" />
                          </div>
                          <h4 className="font-bold mb-2" data-id="element-97">Interest</h4>
                          <p className="text-xs text-slate-500" data-id="element-98">
                            Based on the relevant interbank rate for the asset.
                          </p>
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-slate-200 flex flex-col items-center text-center" data-id="element-99">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4" data-id="element-100">
                            <ShieldCheck className="text-blue-600 w-6 h-6 sm:w-8 sm:h-8" data-id="element-101" />
                          </div>
                          <h4 className="font-bold mb-2 text-sm sm:text-base" data-id="element-102">
                            Equitrade Capital admin fee
                          </h4>
                          <p className="text-xs text-slate-500" data-id="element-103">
                            {activeTab === 'CFD' ? '1% for FX and 3% for other asset classes' : '1.5% for FX and 3.4% for other asset classes'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-6 border-t border-slate-200 pt-6" data-id="element-104">
                      <div data-id="element-105">
                        <h4 className="font-bold text-sm mb-1" data-id="element-106">
                          Currency conversion:
                        </h4>
                        <p className="text-xs text-slate-500" data-id="element-107">
                          We'll convert your funds for a 0.8% fee.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section data-id="element-108">
                  <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12" data-id="element-109">
                    Deposit and withdrawal fees
                  </h2>
                  <div className="space-y-3 sm:space-y-4" data-id="element-110">
                    {depositItems.map((item, i) => <div key={i} className="bg-slate-50 p-4 sm:p-6 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2" data-id="element-111">
                        <div className="max-w-xl" data-id="element-112">
                          <h4 className="font-bold text-sm" data-id="element-113">{item.label}</h4>
                          {item.sub && <p className="text-xs text-slate-500 mt-1" data-id="element-114">
                              {item.sub}
                            </p>}
                        </div>
                        <span className="text-green-600 font-bold text-sm whitespace-nowrap" data-id="element-115">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </section>
              </div>}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12 sm:py-20 mt-16 sm:mt-24" data-id="element-116">
        <div className="max-w-7xl mx-auto px-4" data-id="element-117">
          <div className="flex flex-col items-center space-y-8 sm:space-y-12" data-id="element-118">
            <img src="https://cdn.magicpatterns.com/uploads/edgrKknqbze4NWL7S1gFdy/Screenshot_2026-06-05_at_15.33.56.png" alt="Equitrade Capital" className="h-16 sm:h-24 object-contain" data-id="element-119" />
            <div className="text-[10px] sm:text-[11px] text-slate-500 leading-relaxed text-center max-w-4xl space-y-4 px-4" data-id="element-120">
              <p data-id="element-121">
                Equitrade Capital Ltd is authorised and regulated by the
                Financial Conduct Authority with a registered company in England
                and Wales under number 09969440. Registered Office: 271 Hagley
                Road, Birmingham, United Kingdom, B16 9NB
              </p>
              <p data-id="element-122">
                Equitrade Capital Limited is a trading name of Quantfury Trading
                UK Limited which is authorised and regulated by the Financial
                Conduct Authority of the United Kingdom
              </p>
            </div>
          </div>
        </div>
      </footer>
    </motion.div>;
}